import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Hls from "hls.js";
import {
  Maximize2, Play, Pause, ChevronRight, Volume2, Youtube,
} from "lucide-react";
import { api } from "@/lib/api";
import LiveBadge from "@/components/LiveBadge";
import AdBanner from "@/components/AdBanner";
import AudienceHeartbeat from "@/components/AudienceHeartbeat";
import LiveAudienceCounter from "@/components/LiveAudienceCounter";
import { ExpandableDescription } from "@/components/ExpandableDescription";
import { TV } from "@/constants/testIds";
import { useAudio } from "@/context/AudioContext";

const TVLive = () => {
  const [live, setLive] = useState(null);
  const [plIdx, setPlIdx] = useState(0);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const iframeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const { stop: stopAudio } = useAudio();

  useEffect(() => {
    api.get("/live/tv").then((r) => setLive(r.data)).catch(() => {});
  }, []);

  const playlist = useMemo(() => {
    // channel_videos (from YouTube RSS) takes priority over admin-defined playlist
    if (live?.channel_videos && live.channel_videos.length > 0) return live.channel_videos;
    return live?.playlist_videos || [];
  }, [live]);
  const hasPlaylist = playlist.length > 0;
  const isChannelSync = (live?.channel_videos || []).length > 0;

  // The video currently displayed: either a playlist item OR the base live config
  const currentSource = useMemo(() => {
    if (hasPlaylist) {
      const v = playlist[plIdx % playlist.length];
      return {
        source_type: v.source_type || "hls",
        youtube_id: v.youtube_id,
        stream_url: v.video_url,
        program_name: v.title,
        description: v.description || `${v.program || v.author || ""} · ${v.title}`,
      };
    }
    return {
      source_type: live?.source_type || "hls",
      youtube_id: live?.youtube_id,
      stream_url: live?.stream_url,
      program_name: live?.program_name,
      description: live?.description,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, plIdx, hasPlaylist, playlist.length]);

  const isYT = currentSource.source_type === "youtube" && currentSource.youtube_id;

  const advance = () => {
    if (hasPlaylist) setPlIdx((i) => (i + 1) % playlist.length);
  };

  // HLS/MP4 setup — route .m3u8 through hls.js, MP4/progressive directly to <video>
  useEffect(() => {
    if (isYT) return;
    if (!videoRef.current) return;
    const video = videoRef.current;
    const src = currentSource.stream_url;
    if (!src) return;

    const isHls = /\.m3u8($|\?)/i.test(src);

    if (!isHls) {
      // Progressive MP4 / any non-HLS file: assign directly
      video.src = src;
      setReady(true);
      return;
    }

    // HLS manifest
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      setReady(true);
    } else if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => setReady(true));
    }
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isYT, currentSource.stream_url, plIdx]);

  const ytPost = (func, args = []) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  };

  // Listen to YouTube postMessage — advance on playerState=0 (ended)
  useEffect(() => {
    if (!isYT) return;
    setAudioOn(false);

    const onMessage = (e) => {
      const iframe = iframeRef.current;
      if (!iframe || e.source !== iframe.contentWindow) return;
      let data = e.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      const info = data?.info;
      if (!info) return;
      if (typeof info.muted === "boolean") setAudioOn(!info.muted);
      if (info.playerState === 0) {
        // Ended — go to next in playlist (loop). If no playlist, ignore.
        if (hasPlaylist) advance();
      }
    };
    window.addEventListener("message", onMessage);

    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += 500;
      const w = iframeRef.current?.contentWindow;
      if (w) w.postMessage(JSON.stringify({ event: "listening" }), "*");
      if (elapsed >= 800 && elapsed <= 3000) {
        ytPost("unMute");
        ytPost("setVolume", [100]);
      }
      if (elapsed >= 8000) clearInterval(timer);
    }, 500);

    return () => {
      window.removeEventListener("message", onMessage);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYT, currentSource.youtube_id, hasPlaylist]);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      stopAudio();
      try { await v.play(); setPlaying(true); } catch {}
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  const enableAudio = () => {
    ytPost("unMute");
    ytPost("setVolume", [100]);
    ytPost("playVideo");
    setAudioOn(true);
  };

  const onVideoEnded = () => {
    // HLS/MP4 ended — advance in playlist. If no playlist, loop the same video.
    if (hasPlaylist) advance();
    else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <AudienceHeartbeat channel="tv" />
      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        TV · Streaming
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-6">
        {live?.is_live === true ? "TV ao vivo" : "Programação"}
      </h1>

      <div className="relative rounded-2xl overflow-hidden bg-black shadow-[0_10px_40px_rgba(13,27,75,0.2)]">
        <div className="relative aspect-video w-full">
          {isYT ? (
            <iframe
              key={currentSource.youtube_id}
              data-testid={TV.youtubeFrame}
              ref={iframeRef}
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${currentSource.youtube_id}?autoplay=1&mute=1&enablejsapi=1&rel=0&modestbranding=1&playsinline=1`}
              title={currentSource.program_name}
              allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <video
              key={currentSource.stream_url}
              data-testid={TV.video}
              ref={videoRef}
              className="w-full h-full object-contain bg-black"
              controls
              autoPlay
              playsInline
              poster={live?.cover_url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={onVideoEnded}
            />
          )}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-2 pointer-events-none">
            {live?.is_live === true && <LiveBadge testId={TV.liveBadge} />}
            <LiveAudienceCounter channel="tv" />
          </div>

          {isYT && !audioOn && (
            <button
              data-testid="tv-enable-audio"
              onClick={enableAudio}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs shadow-[0_8px_24px_rgba(215,38,56,0.45)]"
              aria-label="Ativar áudio"
            >
              <Volume2 className="w-4 h-4" />
              Ativar áudio
            </button>
          )}
        </div>

        <div className="p-4 sm:p-5 flex items-center gap-3 bg-[var(--fi-blue)] text-white">
          {!isYT && (
            <button
              data-testid={TV.playPause}
              onClick={togglePlay}
              disabled={!ready}
              className="w-12 h-12 rounded-full bg-[var(--fi-red)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-60"
              aria-label={playing ? "Pausar TV" : "Tocar TV"}
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">
              {live?.is_live === true ? "No ar" : "TV"}
            </div>
            <div className="font-display text-lg sm:text-xl font-extrabold uppercase truncate">
              {currentSource.program_name || "Fala Itapira TV"}
            </div>
          </div>
          {!isYT && (
            <button
              onClick={goFullscreen}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Tela cheia"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <ExpandableDescription
        text={currentSource.description || "Programação ao vivo com jornalismo, esporte e cultura."}
        className="mt-4 max-w-2xl"
      />

      {/* Channel videos — auto-synced from YouTube. Single button, kept clean. */}
      {isChannelSync && (
        <div className="mt-8" data-testid="tv-channel-cta">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-2xl border-2 border-dashed border-[var(--fi-red)]/40 bg-gradient-to-br from-white to-red-50/40">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
                Do canal · Auto-sync
              </div>
              <div className="font-display text-xl sm:text-2xl font-black uppercase text-[var(--fi-blue)] leading-tight">
                Últimos vídeos publicados
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {playlist.length} vídeo{playlist.length !== 1 ? "s" : ""} sincronizado{playlist.length !== 1 ? "s" : ""} automaticamente do canal
              </div>
            </div>
            <a
              href="https://www.youtube.com/@falaitapira/videos"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tv-latest-videos-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap"
            >
              <Youtube className="w-4 h-4" />
              Ver últimos vídeos
            </a>
          </div>
        </div>
      )}

      <AdBanner placement="tv" className="mt-6" />

      <Link
        to="/tv/biblioteca"
        data-testid={TV.goLibrary}
        className="mt-6 flex items-center justify-between rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.08)] hover:shadow-[0_12px_36px_rgba(13,27,75,0.12)] transition-shadow group"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
            Videoteca
          </div>
          <div className="font-display text-2xl font-extrabold uppercase text-[var(--fi-blue)]">
            Ver vídeos gravados
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Reportagens, documentários e programas antigos.
          </div>
        </div>
        <ChevronRight className="w-8 h-8 text-[var(--fi-blue)] group-hover:translate-x-1 transition-transform" />
      </Link>
    </section>
  );
};

export default TVLive;
